import { Manufacturer } from "@juicy/models";
import { AddManufacturerDto } from "@juicy/models/dto";
import { useCallback, useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import { apiService } from "../../../App";
import { Option } from "../../common/autocomplete";
import {
    ManufacturerIsLoadingAtom,
    ManufacturerIsSavingAtom,
    ManufacturerModalVisibleAtom,
    ManufacturerOptionsAtom,
    ManufacturerSearchStringAtom,
    ManufacturerSelectedOptionAtom,
} from "./manufacturerAtoms";

export const useCreateManufacturer = () => {
    const [modalVisible, setModalVisible] = useRecoilState(
        ManufacturerModalVisibleAtom
    );
    const [isSaving, setIsSaving] = useRecoilState(ManufacturerIsSavingAtom);
    const [selectedOption, setSelectedOption] = useRecoilState(
        ManufacturerSelectedOptionAtom
    );
    const [options, setOptions] = useRecoilState(ManufacturerOptionsAtom);
    const [isLoading, setIsLoading] = useRecoilState(ManufacturerIsLoadingAtom);
    const [searchString, setSearchString] = useRecoilState(
        ManufacturerSearchStringAtom
    );
    const timer = useRef<NodeJS.Timer | null>(null);

    const parseManufacturerToOption = (manufacturer: Manufacturer): Option => {
        return {
            label: `${manufacturer.shortname} - ${manufacturer.longname}`,
            value: manufacturer.id.toString(),
            selected: false,
        };
    };

    const searchManufacturers = useCallback(async (search: string) => {
        const res = await apiService.manufacturer.list(1, { search });
        const extraOption: Option = {
            value: "-1",
            label: `Create new manufacturer: ${searchString}`,
            selected: false,
        };
        const options = res.list.map(parseManufacturerToOption);
        if (searchString.length > 1) {
            setOptions([...options, extraOption]);
        } else {
            setOptions(options);
        }
    }, [searchString, setOptions]);

    const onInputChange = async (value: string) => {
        setSearchString(value);
    };

    const onOptionSelected = (option: Option) => {
        setSelectedOption(option);
    };

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(async () => {
            setIsLoading(true);
            await searchManufacturers(searchString);
            setIsLoading(false);
        }, 300);
    }, [searchString, searchManufacturers, setIsLoading]);

    const save = async (data: AddManufacturerDto): Promise<Manufacturer> => {
        setIsSaving(true);
        const savedManufacturer = await apiService.manufacturer.create({
            shortname: data.shortname,
            longname: data.longname,
        });
        setIsSaving(false);
        return savedManufacturer.data;
    };

    return {
        save,
        modalVisible,
        setModalVisible,
        isSaving,
        selectedOption,
        setSelectedOption,
        options,
        isLoading,
        searchString,
        onInputChange,
        onOptionSelected,
    };
};
